import { ref, readonly, type Ref } from 'vue'

/**
 * 答题调试日志（PM 友好版）
 * ----------------------------------------------------------------
 * 这是一个全局共享的"事件池"，store 和组件都可以往里写，
 * 然后答题卡片底部的小面板会实时显示这些事件，方便不熟悉浏览器
 * 控制台的产品同学直接看屏幕上的"诊断信息"来反馈问题。
 *
 * 同时也会把日志打到浏览器 Console（带 [AI-DEBUG] 前缀），方便
 * 技术人员一起对照。
 * ----------------------------------------------------------------
 */

export interface AiDebugEntry {
  /** 自增序号 */
  seq: number
  /** 时间戳（毫秒） */
  time: number
  /** 来源（store / card / stream）。
   *  - `store`: store 层普通状态变更/动作
   *  - `card`:  答题卡片 UI 行为
   *  - `stream`: SSE 流事件校验/降级路径，单独切出来便于在调试面板上区分"流式管道问题"和"业务状态问题"
   */
  source: 'store' | 'card' | 'stream'
  /** 时间字符串 HH:MM:SS.mmm 方便人眼读 */
  timeStr: string
  /** 事件名 */
  msg: string
  /** 附加数据（可选） */
  data?: unknown
}

const _entries: Ref<AiDebugEntry[]> = ref([])
let _seq = 0

function formatTime(d: Date): string {
  const pad = (n: number, w = 2): string => String(n).padStart(w, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(d.getMilliseconds(), 3)}`
}

export function pushAiDebug(source: 'store' | 'card' | 'stream', msg: string, data?: unknown): void {
  const now = new Date()
  const entry: AiDebugEntry = {
    seq: ++_seq,
    time: now.getTime(),
    source,
    timeStr: formatTime(now),
    msg,
    data
  }
  _entries.value.push(entry)
  // 最多保留 80 条
  if (_entries.value.length > 80) {
    _entries.value.splice(0, _entries.value.length - 80)
  }
  // 同时打到控制台（带前缀，方便过滤）
  // eslint-disable-next-line no-console
  console.log('[AI-DEBUG]', `[${source}]`, msg, data ?? '')
}

export function clearAiDebug(): void {
  _entries.value = []
  _seq = 0
}

export const aiDebugEntries = readonly(_entries)
